import { IonIcon, IonItem, IonLabel } from '@ionic/react';
import { documentOutline } from 'ionicons/icons';
import { Form, Formik, FormikProps } from 'formik';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { GenericMessages } from '../../intl/GenericMessages';
import { JobMessages } from '../../intl/JobMessages';
import { DqText } from '../dq-text';
import FileUploadForm from '../reusable-components/file-upload-form/FileUploadForm';
import './LogUploadModal.scss';
import { AppModal } from '../reusable-components/app-modal/AppModal';
import { Input } from '../reusable-components/Input';

export interface LogUploadFormFields {
  description: string;
}

interface LogUploadModalProps {
  /** Whether the modal is open. */
  isOpen: boolean;
  /** Triggers the request to close the modal. */
  onClose: () => void;
  /** Triggers when the form is submitted. */
  onSave: (formInfo: LogUploadFormFields, fileInfo: File) => void;
}

export const LogUploadModal: React.FC<LogUploadModalProps> = props => {
  const { formatMessage } = useIntl();
  const [fileInfo, setFileInfo] = useState<File | undefined>(undefined);
  const [fileRequiredError, setFileRequiredError] = useState<boolean>(false);

  const onSubmission = (info?: LogUploadFormFields) => {
    if (!info || !fileInfo) {
      setFileRequiredError(true);
      return;
    }

    props.onSave(info, fileInfo);
  };

  const onClose = () => {
    setFileInfo(undefined);
    setFileRequiredError(false);
    props.onClose();
  };

  return (
    <Formik
      initialValues={{
        description: '',
      }}
      onSubmit={(values: LogUploadFormFields) => onSubmission(values)}
      enableReinitialize={true}
    >
      {(formikProps: FormikProps<LogUploadFormFields>) => (
        <Form>
          <AppModal
            isOpen={props.isOpen}
            title={formatMessage(JobMessages.uploadJobModalHeader)}
            onClose={onClose}
            primaryButton={{
              label: formatMessage(GenericMessages.submitButtonLabel),
              onClick: () => formikProps.handleSubmit(),
            }}
          >
            <input style={{ display: 'none' }} autoFocus />
            {!fileInfo ? (
              <>
                <FileUploadForm onSave={uploadedFile => setFileInfo(uploadedFile)} acceptableFileTypes=".log" />
                {fileRequiredError && (
                  <DqText className="log-required-error" variant="body-strong">
                    {formatMessage(JobMessages.uploadFormSelectLogPlaceholder)}
                  </DqText>
                )}
              </>
            ) : (
              <IonItem>
                <IonIcon icon={documentOutline} />
                <IonLabel class="ion-padding-start">
                  <DqText variant="body">{fileInfo.name}</DqText>
                </IonLabel>
              </IonItem>
            )}
            <Input
              label={formatMessage(JobMessages.uploadFormDescriptionLabel)}
              value={formikProps.values.description}
              placeholder={formatMessage(JobMessages.uploadFormDescriptionPlaceholder)}
              onChange={formikProps.handleChange}
              name="description"
              id="upload-log-description"
            />
          </AppModal>
        </Form>
      )}
    </Formik>
  );
};
